import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Handshake, Wrench } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp, FadeIn, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ServiceCards } from "../components/ServiceCards";
import { PAINS, THREE_PILLARS, HOW_WE_WORK, STATS, WHO_WE_HELP, WHO_CHIPS } from "../data/content";

const WHY = [
    { icon: TrendingUp, title: "Operator-led advice", desc: "Practical commercial advice from an active operator, with 16+ years of personal commercial experience and 20+ global brands distributed across NZ retail and wholesale." },
    { icon: Handshake, title: "NZ distribution experience", desc: "Hands-on experience in retail distribution, buyer relationships, importation, wholesale pricing and market entry." },
    { icon: Wrench, title: "Hands-on implementation", desc: "The engagement doesn't stop at recommendations — priorities are worked through alongside you and your team." },
];

export default function Home() {
    return (
        <div data-testid="home-page">
            <Seo
                title="We Don't Just Advise. We Help Businesses Grow. | GrowVise NZ"
                description="Commercial growth and implementation for NZ retail, wholesale and distribution businesses. Advisory Retainers and hands-on Implementation Projects. Book a free Growth Conversation."
                path="/"
            />

            {/* HERO — navy */}
            <section className="bg-navy-950 pt-32 sm:pt-40 pb-16 sm:pb-20" data-testid="hero-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeIn>
                        <p className="eyebrow" data-testid="hero-eyebrow">Commercial Growth &amp; Implementation · New Zealand</p>
                        <h1
                            className="mt-6 font-serif font-semibold text-white tracking-tight text-4xl sm:text-5xl lg:text-[3.1rem] leading-[1.18] max-w-[46rem]"
                            data-testid="hero-headline"
                        >
                            We Don't Just Advise. <span className="text-gold-400">We Help Businesses Grow.</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl" data-testid="hero-subcopy">
                            GrowVise works with established New Zealand product businesses through two services: an ongoing Advisory Retainer, and hands-on Implementation Projects. We help owners improve sales, margins, distribution and execution, then work alongside them to get it done.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                            <Link to="/book-a-conversation" className="btn-gold" data-testid="hero-book-cta">
                                Book a Growth Conversation
                            </Link>
                            <Link
                                to="/business-health-check"
                                className="inline-flex items-center gap-2 text-sm font-medium text-gold-400 hover:text-gold-300 transition-colors duration-200"
                                data-testid="hero-healthcheck-cta"
                            >
                                Try the free Health Check <ArrowRight size={15} />
                            </Link>
                        </div>
                    </FadeIn>

                    <FadeUp delay={0.15} className="mt-14 sm:mt-16">
                        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10" data-testid="hero-stats">
                            {STATS.map((s) => (
                                <div key={s.label} className="py-6 pr-6 md:border-r border-white/10 last:border-r-0 md:pl-6 first:pl-0">
                                    <p className={`font-serif font-semibold text-gold-400 ${s.value.length > 12 ? "text-base sm:text-lg leading-snug pt-1" : "text-2xl"}`}>{s.value}</p>
                                    <p className="text-[11px] font-mono tracking-widest uppercase text-slate-500 mt-1.5">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            <Marquee />

            {/* WHO WE HELP — cream */}
            <section className="section-pad bg-cream" data-testid="who-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
                    <FadeUp className="lg:col-span-5">
                        <p className="eyebrow-light">Who we help</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            Built for established NZ product businesses.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="lg:col-span-6 lg:col-start-7">
                        <p className="text-warmgrey leading-relaxed text-base sm:text-lg" data-testid="who-copy">{WHO_WE_HELP}</p>
                        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="who-chips">
                            {WHO_CHIPS.map((c) => (
                                <span key={c} className="font-mono text-[11px] tracking-widest uppercase text-navy-800 border border-navy-900/20 rounded-full px-4 py-2 bg-white/60">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* PROBLEMS — white, editorial list */}
            <section className="section-pad bg-white border-t border-navy-900/10" data-testid="problem-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <FadeUp className="lg:sticky lg:top-28">
                            <p className="eyebrow-light">The problems we solve</p>
                            <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                                Growth gets harder when the business gets bigger.
                            </h2>
                            <p className="mt-5 text-warmgrey leading-relaxed">
                                If two or more of these sound familiar, a short conversation is the fastest way to find out what's actually constraining the business.
                            </p>
                            <Link to="/book-a-conversation" className="btn-outline-ink mt-8" data-testid="problem-book-cta">
                                Book a Growth Conversation <ArrowRight size={15} />
                            </Link>
                        </FadeUp>
                    </div>
                    <Stagger className="lg:col-span-6 lg:col-start-7">
                        {PAINS.map((pain, i) => (
                            <StaggerItem key={pain}>
                                <div className="flex gap-6 py-5 border-t border-navy-900/10 first:border-t-0 first:pt-0">
                                    <span className="font-mono text-sm text-gold-700 pt-0.5 shrink-0 w-7">{String(i + 1).padStart(2, "0")}</span>
                                    <p className="text-ink/90 leading-relaxed">{pain}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* THREE PILLARS — navy */}
            <section className="section-pad bg-navy-900" data-testid="pillars-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl">
                        <p className="eyebrow">What we focus on</p>
                        <h2 className="mt-5 font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Three pillars. Every engagement.
                        </h2>
                    </FadeUp>
                    <Stagger className="mt-14 grid md:grid-cols-3 gap-x-10 gap-y-12">
                        {THREE_PILLARS.map((p) => (
                            <StaggerItem key={p.id}>
                                <div className="border-t-2 border-gold-500/70 pt-6" data-testid={`pillar-card-${p.id}`}>
                                    <h3 className="font-serif font-semibold text-white text-2xl">{p.title}</h3>
                                    <p className="font-mono text-[11px] tracking-widest uppercase text-gold-400 mt-2">{p.tag}</p>
                                    <p className="text-sm text-slate-300/85 leading-relaxed mt-4">{p.desc}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* HOW WE WORK — mist */}
            <section className="section-pad bg-mist" data-testid="how-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl">
                        <p className="eyebrow-light">How we work</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            Conversation first. Plan second. Implementation always.
                        </h2>
                        <p className="mt-5 text-warmgrey leading-relaxed">
                            No mandatory assessment before booking — start with a free conversation and we'll agree the right next step together.
                        </p>
                    </FadeUp>
                    <Stagger className="mt-14 grid md:grid-cols-3 gap-x-10 gap-y-12">
                        {HOW_WE_WORK.map((j) => (
                            <StaggerItem key={j.step}>
                                <div data-testid={`how-step-${j.step}`}>
                                    <p className="font-serif font-semibold text-5xl text-navy-900/15">{j.step}</p>
                                    <h3 className="font-serif font-semibold text-ink text-xl mt-4">{j.title}</h3>
                                    <p className="text-sm text-warmgrey leading-relaxed mt-3">{j.desc}</p>
                                    <p className="mt-5 inline-block font-mono text-[11px] tracking-widest uppercase text-gold-700 border border-gold-600/40 bg-white/70 rounded-full px-3 py-1.5">{j.tag}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* SERVICES — cream */}
            <section className="section-pad bg-cream border-t border-navy-900/10" data-testid="offers-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="flex flex-wrap items-end justify-between gap-6">
                        <div className="max-w-2xl">
                            <p className="eyebrow-light">Two ways to work together</p>
                            <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                                Ongoing advice, or hands-on delivery. Transparent starting prices.
                            </h2>
                            <p className="mt-5 text-warmgrey leading-relaxed">
                                An <strong className="text-ink font-semibold">Advisory Retainer</strong> gives you a senior commercial advisor every month. An <strong className="text-ink font-semibold">Implementation Project</strong> fixes a specific problem with a defined scope, timeframe and result. Many clients start with a project and move to a retainer.
                            </p>
                        </div>
                        <Link to="/how-we-help" className="btn-outline-ink" data-testid="offers-overview-btn">
                            How we help <ArrowRight size={15} />
                        </Link>
                    </FadeUp>
                    <ServiceCards className="mt-14" />
                </div>
            </section>

            {/* WHY GROWVISE — navy */}
            <section className="section-pad bg-navy-950" data-testid="why-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl">
                        <p className="eyebrow">Why GrowVise</p>
                        <h2 className="mt-5 font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Practical advice, from someone who still does the work.
                        </h2>
                    </FadeUp>
                    <Stagger className="mt-14 grid md:grid-cols-3 gap-x-10 gap-y-12">
                        {WHY.map((w) => (
                            <StaggerItem key={w.title}>
                                <div className="border-t border-white/15 pt-6" data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                    <w.icon size={20} className="text-gold-400" />
                                    <h3 className="font-sans font-semibold text-white mt-4">{w.title}</h3>
                                    <p className="text-sm text-slate-300/85 leading-relaxed mt-3">{w.desc}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* FINAL CTA — navy 900 band */}
            <section className="bg-navy-900 border-t border-white/10" data-testid="final-cta-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
                    <FadeUp className="flex-1">
                        <p className="eyebrow">Free · 30 minutes · No obligation</p>
                        <h2 className="mt-4 font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Tell us what needs to improve.
                        </h2>
                        <p className="mt-4 text-slate-300/85 leading-relaxed max-w-xl">
                            A free Growth Conversation is the fastest way to find out whether GrowVise can help — practical, confidential and with no obligation.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="flex flex-wrap gap-4 shrink-0">
                        <Link to="/book-a-conversation" className="btn-gold" data-testid="final-book-cta">
                            Book a Growth Conversation
                        </Link>
                        <Link to="/business-health-check" className="btn-outline" data-testid="final-healthcheck-cta">
                            Free Business Health Check
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
